import { useId } from 'react'
import { cn } from '../lib/utils'

type Props = {
  className?: string
  markOnly?: boolean
  size?: number
}

/** AFOS 360 mark: Loop 360 ribbon + factory modules (concept D), with E lockup. */
export function AfosLogo({ className, markOnly = false, size = 36 }: Props) {
  const uid = useId().replace(/:/g, '')
  const ribbon = `afosRibbon-${uid}`
  const brick = `afosBrick-${uid}`

  return (
    <div className={cn('inline-flex items-center gap-3', className)}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        aria-hidden="true"
        className="shrink-0"
      >
        <defs>
          <linearGradient
            id={ribbon}
            x1="8"
            y1="10"
            x2="56"
            y2="54"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2EE6C5" />
            <stop offset="0.48" stopColor="#9DB0D4" />
            <stop offset="1" stopColor="#FFB347" />
          </linearGradient>
          <linearGradient
            id={brick}
            x1="24"
            y1="22"
            x2="40"
            y2="42"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#E8F1FF" />
            <stop offset="1" stopColor="#2EE6C5" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        {/* Outer continuous loop (360 / closed-loop ops) */}
        <path
          d="M32 8
             C46 8 54 16 54 28
             C54 40 46 48 34 52
             C42 46 46 38 44 30
             C42 20 36 14 28 14
             C18 14 12 22 12 32
             C12 44 20 54 32 56
             C44 56 54 48 56 36"
          stroke={`url(#${ribbon})`}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="spin-slow origin-center"
          style={{ transformOrigin: '32px 32px' }}
        />

        {/* Inner twist hint — möbius read at small sizes */}
        <path
          d="M20 34 C24 26 30 22 38 24 C44 26 48 32 46 38"
          stroke={`url(#${ribbon})`}
          strokeWidth="1.6"
          strokeLinecap="round"
          opacity="0.55"
        />

        {/* Factory module stack (software factory core) */}
        <rect
          x="24"
          y="24"
          width="16"
          height="5.5"
          rx="1.4"
          fill={`url(#${brick})`}
          stroke="#2EE6C5"
          strokeWidth="1.1"
        />
        <rect
          x="24"
          y="31"
          width="16"
          height="5.5"
          rx="1.4"
          fill={`url(#${brick})`}
          stroke="#9DB0D4"
          strokeWidth="1.1"
        />
        <rect
          x="24"
          y="38"
          width="16"
          height="5.5"
          rx="1.4"
          fill={`url(#${brick})`}
          stroke="#FFB347"
          strokeWidth="1.1"
        />

        {/* Human-gate accent on the loop */}
        <circle cx="48" cy="18" r="2.6" fill="#FFB347" className="pulse-glow" />
      </svg>
      {!markOnly && (
        <div className="min-w-0 leading-tight">
          <div className="font-[family-name:var(--font-display)] text-base font-extrabold tracking-tight text-[var(--color-foam)] sm:text-lg">
            AFOS <span className="text-[var(--color-cyan)]">360</span>
          </div>
          <div className="hidden text-[11px] uppercase tracking-[0.22em] text-[var(--color-mist)] min-[400px]:block">
            App Factory OS
          </div>
        </div>
      )}
    </div>
  )
}
